import type { TextoDaEtapa } from "./SimuladorMeiUi";

// Os textos didáticos das etapas dos três simuladores.
//
// ---------------------------------------------------------------------------
// Por que estão aqui e não no motor de conteúdo (000073)
// ---------------------------------------------------------------------------
// A doutrina da casa é que enunciado de formulário é DADO — as 15 perguntas do
// IR Proposta moram em `irp_perguntas` porque quatro delas diziam "no ano de
// 2025" e expiravam sozinhas em janeiro.
//
// Estes textos são de outra natureza: explicam a Lei nº 8.934/1994, a LC
// 123/2006 e o que é capital social. Não têm temporada. O que TINHA temporada
// no app de origem era a lista de anos da DASN, e essa saiu daqui — virou
// `anosDaDasn()`, derivada do calendário, em `dasMei.ts`.
//
// Se um dia a equipe quiser editar estas explicações sem deploy, o caminho é
// pendurá-las no motor de conteúdo com um card próprio; está anotado no runbook.
// Enquanto ninguém pedir, constante tipada é mais simples e não mente sobre
// quem mantém o texto.
//
// O conteúdo é o do app de origem, que a equipe escreveu. Foi preservado — o
// que mudou é onde vive e como é apresentado.

export const ETAPAS_ABERTURA: Record<number, TextoDaEtapa> = {
  0: {
    titulo: "Acesso gov.br",
    emoji: "🔑",
    corpo: (
      <>
        <p>
          Nesta etapa, simulamos o acesso ao portal gov.br. No fluxo real, é por aqui que o
          governo identifica o empresário antes de prosseguir com a abertura do MEI.
        </p>
        <p>
          Esta é uma simulação educacional: nenhum dado é validado nem enviado a órgãos
          oficiais. Depois de "entrar", você avança para o preenchimento dos dados da empresa.
        </p>
        <p>
          <strong>Não digite a sua senha real do gov.br.</strong> Este campo não valida nada e
          não se conecta a lugar nenhum — informe qualquer palavra. Nenhuma ferramenta de
          verdade, inclusive esta, precisa da sua senha de outro serviço.
        </p>
      </>
    ),
  },
  1: {
    titulo: "Identificação do empresário",
    emoji: "🪪",
    corpo: (
      <>
        <p>
          Nesta etapa, deverão ser informados os dados cadastrais do empresário para
          continuidade do processo de legalização. Alguns campos estão identificados como{" "}
          <strong>"dado público"</strong>, conforme previsto na Lei nº 8.934/1994, que
          regulamenta o Registro Público de Empresas Mercantis no Brasil.
        </p>
        <p>
          Isso significa que determinadas informações cadastrais da empresa e do empresário
          podem ser disponibilizadas para consulta pública pelos órgãos de registro e demais
          entidades competentes, garantindo transparência e publicidade dos atos empresariais.
        </p>
        <p>
          <strong>Capital social</strong> é o valor que representa quanto o dono está
          "colocando" na empresa para ela começar a existir e funcionar. Esse valor pode ser em
          dinheiro, equipamentos, móveis, ferramentas, computador, estoque ou qualquer outro
          recurso usado no trabalho.
        </p>
      </>
    ),
  },
  2: {
    titulo: "Atividades econômicas",
    emoji: "💼",
    corpo: (
      <>
        <p>
          Nesta etapa, serão informadas as atividades econômicas que a empresa irá exercer, por
          meio da seleção da ocupação principal e das ocupações secundárias permitidas para o
          MEI.
        </p>
        <p>
          A <strong>atividade principal</strong> define o ramo principal de atuação da empresa e
          gera automaticamente o respectivo CNAE (Classificação Nacional de Atividades
          Econômicas), utilizado pelos órgãos públicos para enquadramento tributário,
          fiscalização e emissão de licenças.
        </p>
        <p>
          Também é possível adicionar <strong>atividades secundárias</strong>, permitindo que a
          empresa exerça outras atividades complementares dentro das permissões do MEI.
        </p>
        <p>
          É a atividade escolhida que decide quais tributos entram no DAS: umas recolhem{" "}
          <strong>ICMS</strong> (comércio, indústria e transporte intermunicipal), outras{" "}
          <strong>ISS</strong> (serviços), e algumas os dois. A lista consultada aqui é a mesma
          da ferramenta de atividades permitidas e vedadas do MEI PRO.
        </p>
        <p>
          Na seção <strong>Forma de Atuação</strong>, devem ser informadas as modalidades pelas
          quais a empresa exercerá suas atividades:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Estabelecimento fixo</strong> — atendimento em um endereço físico fixo
            (loja, escritório, salão).
          </li>
          <li>
            <strong>Internet</strong> — vendas ou serviços realizados online (site, aplicativo,
            redes sociais, e-commerce).
          </li>
          <li>
            <strong>Em local fixo fora da loja</strong> — atendimento em outro ponto fixo
            diferente da sede (quiosque, estande permanente).
          </li>
          <li>
            <strong>Correio</strong> — vendas enviadas pelos Correios, sem atendimento
            presencial.
          </li>
          <li>
            <strong>Porta a porta, postos móveis ou por ambulantes</strong> — atendimento
            itinerante.
          </li>
          <li>
            <strong>Televenda</strong> — vendas feitas por telefone.
          </li>
          <li>
            <strong>Máquinas automáticas</strong> — venda por máquinas de autoatendimento.
          </li>
        </ul>
      </>
    ),
  },
  3: {
    titulo: "Endereços comercial e residencial",
    emoji: "📍",
    corpo: (
      <>
        <p>
          O <strong>endereço comercial</strong> corresponde ao local onde a empresa exercerá
          suas atividades. Dependendo da atividade escolhida, esse endereço poderá ser utilizado
          pelos órgãos municipais e estaduais para análise de viabilidade, emissão de licenças e
          definição da tributação aplicável.
        </p>
        <p>
          Já o <strong>endereço residencial</strong> refere-se ao endereço pessoal do
          empresário. Caso seja o mesmo do endereço comercial, basta marcar a opção
          correspondente para preenchimento automático.
        </p>
        <p>
          Os campos identificados como <strong>"dado público"</strong> poderão ficar disponíveis
          para consulta pública, conforme previsto na Lei nº 8.934/1994.
        </p>
      </>
    ),
  },
  4: {
    titulo: "Declarações obrigatórias",
    emoji: "📜",
    corpo: (
      <>
        <p>
          Nesta etapa, o empresário deverá concordar com as declarações obrigatórias para
          confirmar que atende às regras legais para abertura do MEI.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Declaração de desimpedimento</strong> — confirma que o empresário pode
            exercer atividade empresarial legalmente e não possui impedimentos para abrir um
            MEI.
          </li>
          <li>
            <strong>Opção pelo Simples Nacional e SIMEI</strong> — solicita o enquadramento no
            regime tributário simplificado do MEI, previsto na Lei Complementar nº 123/2006.
          </li>
          <li>
            <strong>Enquadramento como Microempresa (ME)</strong> — declara que a empresa se
            enquadra nas regras de pequeno porte definidas pela legislação.
          </li>
          <li>
            <strong>Termo de ciência e responsabilidade</strong> — confirma que o empresário
            conhece e seguirá as exigências da Prefeitura, do Estado e demais órgãos, mesmo nos
            casos de dispensa inicial de alvará e licença.
          </li>
          <li>
            <strong>Lei nº 8.934/1994</strong> — estabelece que determinadas informações da
            empresa possuem caráter público e poderão ser consultadas nos órgãos de registro
            empresarial.
          </li>
        </ul>
      </>
    ),
  },
  5: {
    titulo: "Revisão final",
    emoji: "✅",
    corpo: (
      <p>
        Esta é a etapa de confirmação dos dados na abertura do CNPJ MEI. Revise todas as
        informações antes de finalizar o cadastro — no processo real, corrigir depois exige
        alteração cadastral.
      </p>
    ),
  },
  6: {
    titulo: "MEI aberto com sucesso",
    emoji: "🎉",
    corpo: (
      <>
        <p>
          Esta tela aparece após a abertura do MEI ser concluída. Ela confirma que o CNPJ foi
          criado e apresenta os próximos passos importantes para o empreendedor.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Emitir CCMEI</strong> — o Certificado da Condição de Microempreendedor
            Individual é o documento que funciona como comprovante do CNPJ e confirmação oficial
            do registro do MEI.
          </li>
          <li>
            <strong>Acessar CREDMEI</strong> — no portal real, direciona para abertura de conta
            PJ, crédito para MEI, maquininhas e demais serviços financeiros.
          </li>
          <li>
            <strong>Acessar o Simples Nacional</strong> — leva ao portal onde o MEI emite e
            acompanha o DAS, o boleto mensal. Aqui, abre a apuração do DAS deste simulador com o
            CNPJ que você acabou de gerar.
          </li>
        </ul>
      </>
    ),
  },
};

export const ETAPAS_DAS: Record<number, TextoDaEtapa> = {
  0: {
    titulo: "Acesso ao PGMEI",
    emoji: "🔐",
    corpo: (
      <>
        <p>
          O PGMEI é o Programa Gerador do DAS do Microempreendedor Individual. No portal real, o
          acesso é pelo CNPJ — sem senha, protegido por captcha.
        </p>
        <p>
          Informe qualquer CNPJ de 14 dígitos: nada é validado nem consultado. Se você acabou de
          usar a simulação de abertura, use o CNPJ que ela gerou.
        </p>
      </>
    ),
  },
  1: {
    titulo: "O que o PGMEI avisa antes de começar",
    emoji: "⚠️",
    corpo: (
      <>
        <p>
          A contagem da <strong>carência</strong> — a quantidade de contribuições necessárias
          para ter direito aos benefícios previdenciários — começa a partir do{" "}
          <strong>primeiro pagamento em dia</strong>.
        </p>
        <p>
          O MEI, <strong>mesmo sem faturamento</strong>, deve pagar mensalmente o DAS. Não
          existe mês em que o MEI ativo esteja isento.
        </p>
        <p>
          Caso o DAS não tenha sido pago até o vencimento, o MEI deve emitir e pagar um novo DAS
          com acréscimos legais — multa e juros.
        </p>
      </>
    ),
  },
  2: {
    titulo: "Ano-calendário e períodos de apuração",
    emoji: "🗓️",
    corpo: (
      <>
        <p>
          Escolha o ano e marque os meses (períodos de apuração) que quer apurar. Meses
          anteriores à abertura do MEI aparecem como <strong>Indisponíveis</strong>: antes
          daquela data a empresa não existia.
        </p>
        <p>
          O vencimento do DAS é sempre o <strong>dia 20 do mês seguinte</strong> ao período
          apurado — o DAS de janeiro vence em 20 de fevereiro, e o de dezembro em 20 de janeiro
          do ano seguinte.
        </p>
        <p>
          Marcar <strong>MEI Caminhoneiro</strong> troca a alíquota do INSS: o transportador
          autônomo de carga contribui com 12% do salário mínimo, e não com 5% (Lei Complementar
          nº 188/2021). O ICMS e o ISS continuam saindo da atividade escolhida.
        </p>
      </>
    ),
  },
  3: {
    titulo: "DAS gerado",
    emoji: "🧾",
    corpo: (
      <>
        <p>
          O documento traz a composição por tributo, cada um com o seu código de arrecadação:{" "}
          <strong>0151</strong> para o INSS, <strong>0181</strong> para o ICMS e{" "}
          <strong>0125</strong> para o ISS.
        </p>
        <p>
          No portal real, para períodos com informação de benefício previdenciário
          (salário-maternidade, auxílio-doença ou auxílio-reclusão), o valor do INSS é zero.
        </p>
        <p>
          Valores de ICMS e ISS inferiores a R$ 10,00 são acumulados e somados automaticamente
          às apurações seguintes até atingir o mínimo permitido para recolhimento.
        </p>
        <p>
          Quando mais de um período é selecionado, o portal gera um DAS consolidado. Aqui, cada
          período sai numa folha própria para você comparar as composições.
        </p>
      </>
    ),
  },
};

export const ETAPAS_DASN: Record<number, TextoDaEtapa> = {
  0: {
    titulo: "Acesso à DASN-SIMEI",
    emoji: "🔐",
    corpo: (
      <p>
        A Declaração Anual do Simples Nacional para o MEI é acessada pelo CNPJ. Este ambiente é
        apenas demonstração: informe qualquer número de 14 dígitos, pois nada é validado.
      </p>
    ),
  },
  1: {
    titulo: "Iniciar ou retificar a declaração",
    emoji: "📅",
    corpo: (
      <>
        <p>
          Aqui se inicia ou se retifica uma declaração anual do MEI. É necessário selecionar o
          ano-calendário e conferir o tipo de declaração para continuar.
        </p>
        <p>
          <strong>Ano-calendário</strong> é o ano a que se referem as informações declaradas. A
          declaração de um ano é entregue até 31 de maio do ano seguinte — por isso o ano mais
          recente da lista aparece como declaração <em>original</em>, e os anteriores como{" "}
          <em>retificadora</em>. Estão disponíveis os anos não decadentes em que o contribuinte
          constou como optante pelo SIMEI em pelo menos um dia.
        </p>
        <p>
          <strong>Tipo de declaração</strong> define a modalidade enviada: original, ou
          retificação de uma declaração já transmitida.
        </p>
      </>
    ),
  },
  2: {
    titulo: "Preencher o faturamento",
    emoji: "💰",
    corpo: (
      <>
        <p>
          <strong>Receita de comércio e indústria</strong> — a receita bruta total do
          ano-calendário referente a comércio, indústria, serviço de transporte intermunicipal e
          interestadual e fornecimento de refeições. Sem receita nessa atividade, informe zero.
        </p>
        <p>
          <strong>Receita de prestação de serviços</strong> — a receita bruta total de serviços
          de qualquer natureza, exceto transportes intermunicipais e interestaduais. Informe
          também aqui as receitas das ocupações de locador, listadas no Anexo XI da Resolução
          CGSN 140/2018, e demais receitas sem incidência de ISS e ICMS.
        </p>
        <p>
          <strong>Receita bruta total</strong> é preenchida pelo sistema: o somatório dos dois
          campos.
        </p>
        <p>
          <strong>Possuiu empregado no período</strong> — informe se contratou, ou não,
          empregado.
        </p>
      </>
    ),
  },
  3: {
    titulo: "Resumo da declaração",
    emoji: "📋",
    corpo: (
      <>
        <p>
          Este resumo mostra os tributos devidos em cada período de apuração do ano-calendário e
          os DAS que foram pagos.
        </p>
        <p>
          <strong>Valor apurado</strong> é a soma dos valores de cada tributo (INSS, ISS e ICMS),
          ainda que não haja emissão de DAS — por exemplo, quando há informação de benefício
          previdenciário no PGMEI e a apuração fica abaixo de R$ 10,00.
        </p>
        <p>
          <strong>Valor pago</strong> corresponde à soma de todos os pagamentos, em valor
          original, efetuados para cada período.
        </p>
        <p>
          Depois da transmissão não há como alterar informação alguma, a não ser por meio de uma
          declaração retificadora.
        </p>
      </>
    ),
  },
  4: {
    titulo: "Declaração transmitida",
    emoji: "🎉",
    corpo: (
      <p>
        A declaração foi enviada. Guarde o recibo gerado, pois ele serve como comprovante da
        entrega. Depois da transmissão não há como alterar qualquer informação, a não ser por
        meio de uma declaração retificadora.
      </p>
    ),
  },
};
